import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { el } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Inline Calendar Date Picker Component
 * Εμφανίζει το ημερολόγιο πλήρως ανοιχτό (inline)
 * Για χρήση σε επιλογή ημερομηνίας δικασίμου
 */
const InlineDatePicker = ({ 
  value, 
  onChange, 
  id,
  minDate,
  className = "",
  ...props 
}) => {
  // Convert string value to Date object
  const selectedDate = value ? new Date(value) : null;
  
  // Minimum date defaults to today
  const minimumDate = minDate || new Date();
  
  // Manual input state
  const [manualInput, setManualInput] = useState('');
  
  // Sync manual input with selected date
  useEffect(() => {
    if (selectedDate) {
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear();
      setManualInput(`${day}/${month}/${year}`);
    } else {
      setManualInput('');
    }
  }, [value]);

  const handleChange = (date) => {
    if (date) {
      // Convert Date to YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      // Create synthetic event to match native input behavior
      const syntheticEvent = {
        target: {
          value: formattedDate,
          id: id
        }
      };
      onChange(syntheticEvent);
    } else {
      onChange({ target: { value: '', id: id } });
    }
  };
  
  // Handle manual input change
  const handleManualInputChange = (e) => {
    const inputValue = e.target.value;
    setManualInput(inputValue);
    
    // Try to parse the date (format: dd/mm/yyyy)
    const parts = inputValue.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime())) {
          handleChange(date);
        }
      }
    }
  };

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* Manual input field */}
      <input
        type="text"
        value={manualInput}
        onChange={handleManualInputChange}
        placeholder="ηη/μμ/εεεε"
        className="w-full h-8 px-3 py-1 mb-2 border border-slate-300 rounded-md bg-white text-slate-900 text-sm focus:outline-none focus:ring-0 focus:border-slate-400"
        autoComplete="off"
        data-lpignore="true"
      />
      <DatePicker
        id={id}
        selected={selectedDate}
        onChange={handleChange}
        dateFormat="dd/MM/yyyy"
        locale={el}
        inline
        minDate={minimumDate}
        calendarClassName="small-calendar"
        fixedHeight
        {...props}
      />
      {selectedDate && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg w-full">
          <p className="text-sm font-medium text-red-800">
            Επιλεγμένη ημερομηνία: {selectedDate.toLocaleDateString('el-GR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default InlineDatePicker;
